/**
 * Article how-to video data model + JSON-LD helpers.
 *
 * Articles can carry a `seo.video` object describing a Cloudflare Stream how-to
 * video: the series/episode it belongs to, the Stream UID, duration, upload
 * date, presenter, an ordered list of steps, and a full transcript. The public
 * article page renders a lazy player + Steps box + Transcript, and emits
 * VideoObject + HowTo structured data for search engines.
 */
import { getStreamSubdomain } from './cloudflareStream';

/** A single step. Accepts a plain string or a { title/name, text } object. */
export type ArticleVideoStep = string | { title?: string; name?: string; text: string };

export interface ArticleVideo {
  /** Series this video belongs to, e.g. "How-To". */
  series?: string;
  /** Episode number or label within the series. */
  episode?: number | string;
  /**
   * Top-level how-to category (mirrors the service lines), e.g. "erp" or "ai".
   * Used to group how-tos into their category pages. */
  group?: string;
  /** Sub-category slug within the group, e.g. "infor-lawson" or "erp-general". */
  category?: string;
  /** Cloudflare Stream video UID (may be null while a training video only has an MP4). */
  streamId?: string | null;
  /** Total duration in seconds. */
  durationSeconds?: number;
  /** ISO 8601 duration, e.g. PT4M12S. */
  isoDuration?: string;
  /** ISO date (YYYY-MM-DD) the video was published. */
  uploadDate?: string;
  /** Display title of the video. */
  title?: string;
  /** Custom AI-generated poster/thumbnail image URL (falls back to the Stream thumbnail). */
  thumbnailUrl?: string;
  /** Presenter name — an AI avatar is flagged in the UI. */
  presenter?: string;
  /** Written spoken transcript. */
  transcript?: string;
  /** Ordered how-to steps. */
  steps?: ArticleVideoStep[];
  /**
   * Platform / service labels this video touches, e.g. ["Infor CloudSuite",
   * "AI Agents"]. Used by the topic chips on the how-to index.
   */
  platforms?: string[];
  /**
   * Marks a video whose screens are illustrative mockups built from sample
   * data rather than a real product — surfaced with a disclaimer label.
   */
  illustrative?: boolean;
  /** Direct MP4 URL — a fallback player for a training video not yet on Stream. */
  mp4Url?: string;
  /** Explicit poster/thumbnail URL (falls back to the Stream thumbnail). */
  posterUrl?: string;
  /** WebVTT captions URL for the native MP4 player. */
  captionsUrl?: string;
  /** Training level (Fundamentals | Intermediate | Advanced). */
  level?: TrainingLevel | string;
  /** Who the training video is for (e.g. "Employees and clients"). */
  audience?: string;
  /** Chapter markers — clicking one seeks the player to `t` seconds. */
  chapters?: VideoChapter[];
}

/** A single chapter marker within a training video. */
export interface VideoChapter {
  /** Start time, in seconds. */
  t: number;
  title: string;
}

/** Training level for a training-series video. */
export type TrainingLevel = 'Fundamentals' | 'Intermediate' | 'Advanced';

/** The canonical series label for how-to videos. */
export const HOW_TO_SERIES = 'How-To';

/** The canonical series label for long-form training videos. */
export const TRAINING_SERIES = 'Training';

/** Normalises a step (string | {title/name,text}) into { title, text }. */
export function normalizeStep(step: ArticleVideoStep): { title: string; text: string } {
  if (typeof step === 'string') return { title: '', text: step };
  const title = (step?.title || step?.name || '').trim();
  return { title, text: (step?.text || '').trim() };
}

/** Converts a duration in seconds to an ISO 8601 duration string. */
export function toIsoDuration(seconds?: number | null): string {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return '';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  let out = 'PT';
  if (h) out += `${h}H`;
  if (m) out += `${m}M`;
  if (s || (!h && !m)) out += `${s}S`;
  return out;
}

/** Human readable duration label, e.g. 4:12. */
export function formatDuration(seconds?: number | null): string {
  if (!seconds || !Number.isFinite(seconds)) return '';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Poster/thumbnail URL for a Stream UID (empty string when there is no UID). */
export function videoThumbnailUrl(streamId?: string | null, subdomain?: string): string {
  if (!streamId) return '';
  const host = subdomain || getStreamSubdomain();
  return `https://${host}/${streamId}/thumbnails/thumbnail.jpg?time=10s&height=720`;
}

/** Player iframe URL for a Stream UID (empty string when there is no UID). */
export function videoIframeUrl(
  streamId?: string | null,
  subdomain?: string,
  poster?: string,
  autoplay = false,
): string {
  if (!streamId) return '';
  const host = subdomain || getStreamSubdomain();
  const params = new URLSearchParams();
  if (poster) params.set('poster', poster);
  params.set('defaultTextTrack', 'en');
  if (autoplay) {
    params.set('autoplay', 'true');
    params.set('muted', 'true');
  }
  params.set('preload', 'metadata');
  const qs = params.toString();
  return `https://${host}/${streamId}/iframe${qs ? `?${qs}` : ''}`;
}

/** True when a video belongs to the long-form Training series. */
export function isTrainingVideo(video?: ArticleVideo | null): boolean {
  return !!video && video.series === TRAINING_SERIES;
}

/** Formats a chapter timestamp (seconds) as m:ss or h:mm:ss. */
export function formatChapterTime(totalSeconds?: number | null): string {
  if (totalSeconds == null || !Number.isFinite(Number(totalSeconds)) || Number(totalSeconds) < 0) return '0:00';
  const total = Math.floor(Number(totalSeconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h ? String(m).padStart(2, '0') : String(m);
  return `${h ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

/**
 * Builds a VideoObject JSON-LD block for a TRAINING video, with one `Clip`
 * (chapter) per entry in `video.chapters` — each carrying name, startOffset,
 * endOffset and a `#t=` deep link. Also used by the prerenderer so the static
 * HTML and the hydrated DOM emit the identical entity.
 */
export function buildTrainingVideoObjectSchema(
  video: ArticleVideo,
  ctx: VideoContext,
  subdomain?: string,
): Record<string, unknown> {
  const host = subdomain || getStreamSubdomain();
  const thumb =
    video.posterUrl ||
    video.thumbnailUrl ||
    (video.streamId ? videoThumbnailUrl(video.streamId, subdomain) : undefined);
  const contentUrl = video.streamId
    ? `https://${host}/${video.streamId}/manifest/video.m3u8`
    : video.mp4Url || undefined;
  const embedUrl = video.streamId
    ? videoIframeUrl(video.streamId, subdomain)
    : video.mp4Url || undefined;
  const chapters = [...(video.chapters || [])].sort((a, b) => Number(a.t) - Number(b.t));
  const clips = chapters.map((c, i) => {
    const next = chapters[i + 1];
    const start = Math.max(0, Number(c.t) || 0);
    const end = next ? Math.max(0, Number(next.t) || 0) : video.durationSeconds || undefined;
    return {
      '@type': 'Clip',
      name: c.title,
      startOffset: start,
      ...(end !== undefined ? { endOffset: end } : {}),
      ...(embedUrl ? { url: `${ctx.url}#t=${start}` } : {}),
    };
  });
  const iso = video.isoDuration || toIsoDuration(video.durationSeconds);
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    '@id': `${ctx.url}#video`,
    name: video.title || ctx.headline,
    description: ctx.description || video.title || ctx.headline,
    ...(thumb ? { thumbnailUrl: thumb } : {}),
    ...(video.uploadDate ? { uploadDate: video.uploadDate } : {}),
    ...(iso ? { duration: iso } : {}),
    ...(contentUrl ? { contentUrl } : {}),
    ...(embedUrl ? { embedUrl } : {}),
    ...(video.transcript ? { transcript: video.transcript } : {}),
    ...(video.audience ? { audience: { '@type': 'Audience', audienceType: video.audience } } : {}),
    ...(clips.length ? { hasPart: clips } : {}),
    inLanguage: 'en-US',
    publisher: publisher(),
  };
}

/** BreadcrumbList for a training video: Home > Videos > Training > title. */
export function buildTrainingBreadcrumbs(title: string, url: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://cendien.com' },
      { '@type': 'ListItem', position: 2, name: 'Videos', item: 'https://cendien.com/videos' },
      { '@type': 'ListItem', position: 3, name: 'Training', item: 'https://cendien.com/videos/training' },
      { '@type': 'ListItem', position: 4, name: title, item: url },
    ],
  };
}

/** Canonical organiser/publisher reused by schema blocks. */
function publisher() {
  return {
    '@type': 'Organization',
    name: 'Cendien',
    url: 'https://cendien.com',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/8e0505aa0635a0a18473d6b2a0ce13d3a3.png',
    },
  };
}

export interface VideoContext {
  /** Article headline (used for name/description fallbacks). */
  headline: string;
  /** Article meta description. */
  description?: string;
  /** Absolute canonical URL of the article. */
  url: string;
}

/** Builds a VideoObject JSON-LD block for an article video. */
export function buildVideoObjectSchema(
  video: ArticleVideo,
  ctx: VideoContext,
  subdomain?: string,
): Record<string, unknown> {
  const hero =
    video.thumbnailUrl ||
    video.posterUrl ||
    (video.streamId ? videoThumbnailUrl(video.streamId, subdomain) : '');
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    // Stable @id so the matching HowTo block can reference this single
    // VideoObject instead of embedding a duplicate copy — the page must emit
    // exactly ONE VideoObject entity.
    '@id': `${ctx.url}#video`,
    name: video.title || ctx.headline,
    description: ctx.description || video.title || ctx.headline,
    thumbnailUrl: hero,
    uploadDate: video.uploadDate || undefined,
    duration: video.isoDuration || toIsoDuration(video.durationSeconds) || undefined,
    contentUrl: video.streamId
      ? `https://${subdomain || getStreamSubdomain()}/${video.streamId}/manifest/video.m3u8`
      : undefined,
    embedUrl: video.streamId ? videoIframeUrl(video.streamId, subdomain) : undefined,
    transcript: video.transcript || undefined,
    inLanguage: 'en-US',
    publisher: publisher(),
  };
}

/**
 * Builds a HowTo JSON-LD block when the video has steps. The HowTo is named
 * after the ARTICLE (not the video), and links to the matching VideoObject via
 * `@id` (rather than embedding a second, full copy) so the page carries exactly
 * ONE VideoObject and ONE HowTo block — never a duplicated VideoObject.
 */
export function buildHowToSchema(
  video: ArticleVideo,
  ctx: VideoContext,
  videoObject?: Record<string, unknown>,
): Record<string, unknown> | null {
  const steps = (video.steps || []).map(normalizeStep).filter((s) => s.text);
  if (steps.length === 0) return null;
  // Reference the standalone VideoObject by @id when available; otherwise fall
  // back to whatever was passed so no information is ever lost.
  const videoRef =
    videoObject && typeof videoObject['@id'] === 'string'
      ? { '@id': videoObject['@id'] as string }
      : videoObject;
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: ctx.headline,
    description: ctx.description || ctx.headline,
    ...(video.durationSeconds
      ? { totalTime: video.isoDuration || toIsoDuration(video.durationSeconds) }
      : {}),
    ...(videoRef ? { video: videoRef } : {}),
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      ...(s.title ? { name: s.title } : {}),
      text: s.text,
      url: `${ctx.url}#step-${i + 1}`,
    })),
  };
}